import clsx from 'clsx';
import { AdyenCheckoutWrapper } from 'components/payments/AdyenCheckoutWrapper';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { trackCheckoutPageViewed } from 'utils/analytics/analytics';
import { isFacebookApp } from 'utils/ua';

import {
  AdyenApplePay,
  AdyenDebitCard,
  AdyenGooglePay,
  AdyenPayPal,
} from './Checkout.AdyenSvgs';
import styles from './Checkout.module.css';
import { getWalletAvailability } from './CheckoutUtils';

interface Props {}

const Checkout = observer((props: Props) => {
  const enterStore = useEnterStore();
  const userStore = useUserStore();

  useEffect(() => {
    if (userStore.currentUser) {
      (async () => enterStore.maybeContinueToPayment())();
      enterStore.setOrderError(null);
    }
  }, [!!userStore.currentUser]);

  useEffect(() => {
    if (
      enterStore.initialised &&
      userStore.userInitialized &&
      userStore.currentUser
    ) {
      trackCheckoutPageViewed({
        numberOfLines: enterStore.lines.length,
        valueInPence: enterStore.orderCost.cost,
        userId: userStore.currentUser.id,
        rawEmailAddress: userStore.currentUser.emailAddress,
      });
    }
  }, [enterStore.initialised, userStore.userInitialized]);

  const checkoutComponentEnabled = !!userStore.currentUser;

  return (
    <div
      className={clsx(
        styles.container,
        'daymadeTypographySRegular',
        !checkoutComponentEnabled && styles.checkoutDisabled,
      )}
    >
      <p className={clsx(styles.eyebrow, 'daymadeTypographyTaglineAccent')}>
        Choose your payment method
      </p>
      <h2
        className={clsx(
          styles.heading,
          'daymadeTypographyMLBlack textAlignLeft',
        )}
      >
        Complete your membership
      </h2>
      {checkoutComponentEnabled ? (
        <AdyenCheckoutWrapper checkout2026 />
      ) : (
        <DummyPaymentOptions />
      )}
    </div>
  );
});

const DummyPaymentOptions = () => {
  const inFacebookApp = useMemo(() => isFacebookApp(), []);
  const { showApplePay, showGooglePay } = useMemo(
    () => getWalletAvailability(),
    [],
  );

  return (
    <div className={styles.dummyPaymentContainer}>
      <ul>
        {showApplePay && (
          <li>
            <span
              className={clsx(
                styles.dummyPaymentBadge,
                styles.dummyPaymentBadgeNoBorder,
              )}
            >
              <AdyenApplePay />
            </span>
            Apple Pay
          </li>
        )}
        {showGooglePay && !inFacebookApp && (
          <li>
            <span
              className={clsx(
                styles.dummyPaymentBadge,
                styles.dummyPaymentBadgeNoBorder,
              )}
            >
              <AdyenGooglePay />
            </span>
            Google Pay
          </li>
        )}
        <li>
          <span className={styles.dummyPaymentBadge}>
            <AdyenPayPal />
          </span>
          PayPal
        </li>
        <li>
          <span className={styles.dummyPaymentBadge}>
            <AdyenDebitCard />
          </span>
          Debit Card
        </li>
      </ul>
      <p className={styles.dummyPaymentMessage}>
        (Note: Credit Cards not accepted)
      </p>
    </div>
  );
};

export default Checkout;
